import { useAuth } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

import {
	type ConversionFlagKey,
	getConversionFlagReadState,
} from "~/lib/conversion-test-flags";

/** Ad blockers stop PostHog; resolve to the default state rather than wait. */
const FLAG_TIMEOUT_MS = 3000;

/** PostHog ID the currently loaded flags were fetched for. */
let flagsDistinctId: string | undefined;
let isAwaitingIdentifiedFlags = false;
let subscribed = false;
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) listener();
};

/**
 * Marks the loaded flags as belonging to the previous ID. Call right before
 * `posthog.identify` switches to a new ID, so flags are not read until they
 * reload for the signed-in visitor.
 *
 * @returns {void}
 */
export const markConversionFlagsStale = (): void => {
	flagsDistinctId = undefined;
	isAwaitingIdentifiedFlags = true;
	ensureSubscription();
	notify();
};

function ensureSubscription() {
	if (subscribed || typeof window === "undefined") return;
	subscribed = true;

	// PostHog calls back synchronously when flags are already loaded; those
	// may still be the anonymous flags if identify just ran.
	let isSubscribing = true;
	posthog.onFeatureFlags(() => {
		if (isSubscribing && isAwaitingIdentifiedFlags) return;

		isAwaitingIdentifiedFlags = false;
		flagsDistinctId = posthog.get_distinct_id();
		notify();
	});
	isSubscribing = false;
}

/**
 * Reads one conversion-test flag. Reading the flag records exposure, so pass
 * `enabled: false` until the caller would actually use the value.
 *
 * @param {ConversionFlagKey} key - Flag key from `CONVERSION_FLAGS`
 * @param {object} [options]
 * @param {boolean} [options.enabled] - False skips reading the flag
 * @returns {{ value: unknown; ready: boolean }} Raw flag value (undefined when off, loading, or blocked) and whether it is final
 */
const useConversionFlag = (
	key: ConversionFlagKey,
	{ enabled = true }: { enabled?: boolean } = {},
): { value: unknown; ready: boolean } => {
	const { userId } = useAuth();
	const [, setVersion] = useState(0);
	const [hasWaitedTooLong, setHasWaitedTooLong] = useState(false);

	useEffect(() => {
		if (!enabled) return;

		const listener = () => setVersion((version) => version + 1);
		listeners.add(listener);
		ensureSubscription();

		const timeout = window.setTimeout(
			() => setHasWaitedTooLong(true),
			FLAG_TIMEOUT_MS,
		);

		return () => {
			listeners.delete(listener);
			window.clearTimeout(timeout);
		};
	}, [enabled]);

	if (!enabled) return { value: undefined, ready: false };

	const readState = getConversionFlagReadState({
		signedInUserId: userId,
		flagsDistinctId,
		hasWaitedTooLong,
	});

	if (readState === "wait") return { value: undefined, ready: false };
	if (readState === "default") return { value: undefined, ready: true };

	return { value: posthog.getFeatureFlag(key), ready: true };
};

export default useConversionFlag;
